/**
 * FunctionModal Component (Fixed)
 * 
 * A user-friendly function editor with:
 * - Parameter builder form (add/remove parameters visually)
 * - Headers builder form (add/remove headers visually)
 * - Tabs to switch between Form view and JSON view
 * - Telegram preset buttons
 * 
 * File: dashboard/src/components/FunctionModal.jsx
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, ChevronDown, ChevronUp, Code, ListPlus } from 'lucide-react';
import api from '../services/api';

// Parameter types available
const PARAM_TYPES = [
  { value: 'string', label: 'Text (String)' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes/No (Boolean)' },
  { value: 'array', label: 'List (Array)' },
  { value: 'object', label: 'Object' }
];

// Telegram preset configurations
const TELEGRAM_PRESETS = [
  {
    id: 'notify',
    name: '📱 Telegram Notify',
    color: 'blue',
    config: {
      name: 'send_to_telegram',
      description: 'Send collected call information to Telegram chat for review or follow-up',
      api_method: 'POST',
      api_url: 'http://localhost:3000/api/telegram/notify',
      parameters: {
        type: 'object',
        properties: {
          customer_name: { type: 'string', description: 'The customer\'s full name' },
          phone_number: { type: 'string', description: 'Customer phone number' },
          summary: { type: 'string', description: 'Brief summary of the conversation' },
          follow_up_required: { type: 'boolean', description: 'Whether follow-up action is needed' },
          notes: { type: 'string', description: 'Additional notes from the call' }
        },
        required: ['customer_name', 'summary']
      },
      api_headers: {},
      is_active: true
    }
  },
  {
    id: 'lead',
    name: '🎯 Lead Capture',
    color: 'green',
    config: {
      name: 'send_lead_to_telegram',
      description: 'Send qualified lead information to Telegram for sales follow-up',
      api_method: 'POST',
      api_url: 'http://localhost:3000/api/telegram/notify',
      parameters: {
        type: 'object',
        properties: {
          lead_name: { type: 'string', description: 'Name of the potential customer' },
          company: { type: 'string', description: 'Company name if applicable' },
          phone: { type: 'string', description: 'Contact phone number' },
          email: { type: 'string', description: 'Contact email address' },
          interest: { type: 'string', description: 'What product/service they are interested in' },
          budget: { type: 'string', description: 'Budget range if mentioned' },
          urgency: { type: 'string', description: 'How urgent is their need (low/medium/high)' }
        },
        required: ['lead_name', 'phone', 'interest']
      },
      api_headers: {},
      is_active: true
    }
  },
  {
    id: 'order',
    name: '📦 Order Update',
    color: 'purple',
    config: {
      name: 'send_order_to_telegram',
      description: 'Send order confirmation or update details to Telegram',
      api_method: 'POST',
      api_url: 'http://localhost:3000/api/telegram/notify',
      parameters: {
        type: 'object',
        properties: {
          customer_name: { type: 'string', description: 'Customer name' },
          order_id: { type: 'string', description: 'Order ID or reference number' },
          order_status: { type: 'string', description: 'Status: confirmed/pending/cancelled/modified' },
          items: { type: 'string', description: 'Items ordered or modified' },
          total_amount: { type: 'string', description: 'Total order amount' },
          delivery_date: { type: 'string', description: 'Expected delivery date' }
        },
        required: ['customer_name', 'order_status']
      },
      api_headers: {},
      is_active: true
    }
  }
];

// WhatsApp preset — one-click "send to WhatsApp" tool.
// injectApiKey makes applyPreset auto-fill the x-api-key header with the
// tenant's API key, so the user doesn't have to enter anything.
const WHATSAPP_PRESETS = [
  {
    id: 'wa_send',
    name: '💬 Send to WhatsApp',
    color: 'green',
    injectApiKey: true,
    config: {
      name: 'send_whatsapp',
      description:
        "Send a WhatsApp message to the customer during the call. Use when the customer asks for information to be sent to their WhatsApp. Defaults to the caller's own number; only set \"number\" if the customer gives a different one.",
      api_method: 'POST',
      api_url: 'http://localhost:4000/api/whatsapp/send',
      parameters: {
        type: 'object',
        properties: {
          message: { type: 'string', description: 'The exact text to send to the customer on WhatsApp' },
          number: { type: 'string', description: 'Optional. Only set if the customer asks to send to a different number, international format e.g. 923001234567' }
        },
        required: ['message']
      },
      api_headers: {},
      is_active: true
    }
  }
];

// Helper: Convert JSON schema to list format
const schemaToList = (parameters) => {
  if (!parameters?.properties) return [];
  const required = parameters.required || [];
  return Object.entries(parameters.properties).map(([name, config]) => ({
    name,
    type: config.type || 'string',
    description: config.description || '',
    required: required.includes(name)
  }));
};

// Helper: Convert list to JSON schema format
const listToSchema = (list) => {
  const properties = {};
  const required = [];
  
  list.forEach(param => {
    if (param.name && param.name.trim()) {
      const key = param.name.trim();
      properties[key] = {
        type: param.type || 'string',
        description: param.description || ''
      };
      if (param.required) {
        required.push(key);
      }
    }
  });
  
  return { type: 'object', properties, required };
};

// Helper: Convert headers object to list format
const headersToList = (headers) => {
  if (!headers || typeof headers !== 'object') return [];
  return Object.entries(headers).map(([key, value]) => ({ key, value }));
};

// Helper: Convert list to headers object
const listToHeaders = (list) => {
  const obj = {};
  list.forEach(h => {
    if (h.key && h.key.trim()) {
      obj[h.key.trim()] = h.value || '';
    }
  });
  return obj;
};

export default function FunctionModal({ 
  isOpen, 
  onClose, 
  editingFunction, 
  setEditingFunction,
  onSave,
  onDelete 
}) {
  const [paramsTab, setParamsTab] = useState('form');
  const [headersTab, setHeadersTab] = useState('form');
  const [jsonParamsText, setJsonParamsText] = useState('');
  const [jsonHeadersText, setJsonHeadersText] = useState('');
  const [jsonError, setJsonError] = useState(null);
  
  // Local state for form-based editing
  const [paramsList, setParamsList] = useState([]);
  const [headersList, setHeadersList] = useState([]);

  // Initialize local state when modal opens or editingFunction changes
  useEffect(() => {
    if (isOpen) {
      const params = schemaToList(editingFunction?.parameters);
      const headers = headersToList(editingFunction?.api_headers);
      setParamsList(params);
      setHeadersList(headers);
      setJsonParamsText(JSON.stringify(editingFunction?.parameters || { type: 'object', properties: {}, required: [] }, null, 2));
      setJsonHeadersText(JSON.stringify(editingFunction?.api_headers || {}, null, 2));
      setJsonError(null);
    }
  }, [isOpen]); // Only run when modal opens/closes

  // Ensure editingFunction has a default structure
  const getFunction = useCallback(() => {
    return editingFunction || {
      name: '',
      description: '',
      api_method: 'POST',
      api_url: '',
      content_type: 'application/json',
      parameters: { type: 'object', properties: {}, required: [] },
      api_headers: {},
      is_active: true
    };
  }, [editingFunction]);

  // Update function field
  const updateField = useCallback((field, value) => {
    const current = getFunction();
    setEditingFunction({ ...current, [field]: value });
  }, [getFunction, setEditingFunction]);

  // ==================== PARAMETER HANDLERS ====================
  
  const addParameter = useCallback(() => {
    const newParam = { name: '', type: 'string', description: '', required: false };
    const newList = [...paramsList, newParam];
    setParamsList(newList);
    
    // Update the function's parameters
    const current = getFunction();
    setEditingFunction({
      ...current,
      parameters: listToSchema(newList)
    });
  }, [paramsList, getFunction, setEditingFunction]);

  const updateParameter = useCallback((index, field, value) => {
    const newList = paramsList.map((param, i) => {
      if (i === index) {
        return { ...param, [field]: value };
      }
      return param;
    });
    setParamsList(newList);
    
    // Update the function's parameters
    const current = getFunction();
    setEditingFunction({
      ...current,
      parameters: listToSchema(newList)
    });
  }, [paramsList, getFunction, setEditingFunction]);

  const removeParameter = useCallback((index) => {
    const newList = paramsList.filter((_, i) => i !== index);
    setParamsList(newList);
    
    // Update the function's parameters
    const current = getFunction();
    setEditingFunction({
      ...current,
      parameters: listToSchema(newList)
    });
  }, [paramsList, getFunction, setEditingFunction]);

  const moveParameter = useCallback((index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= paramsList.length) return;
    
    const newList = [...paramsList];
    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
    setParamsList(newList);
    
    // Update the function's parameters
    const current = getFunction();
    setEditingFunction({
      ...current,
      parameters: listToSchema(newList)
    });
  }, [paramsList, getFunction, setEditingFunction]);

  // Handle JSON text change for parameters
  const handleParamsJsonChange = useCallback((text) => {
    setJsonParamsText(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      const current = getFunction();
      setEditingFunction({ ...current, parameters: parsed });
      setParamsList(schemaToList(parsed));
    } catch (err) {
      setJsonError('Invalid JSON format');
    }
  }, [getFunction, setEditingFunction]);

  // ==================== HEADER HANDLERS ====================

  const addHeader = useCallback(() => {
    const newHeader = { key: '', value: '' };
    const newList = [...headersList, newHeader];
    setHeadersList(newList);
    
    // Update the function's headers
    const current = getFunction();
    setEditingFunction({
      ...current,
      api_headers: listToHeaders(newList)
    });
  }, [headersList, getFunction, setEditingFunction]);

  const updateHeader = useCallback((index, field, value) => {
    const newList = headersList.map((header, i) => {
      if (i === index) {
        return { ...header, [field]: value };
      }
      return header;
    });
    setHeadersList(newList);
    
    // Update the function's headers
    const current = getFunction();
    setEditingFunction({
      ...current,
      api_headers: listToHeaders(newList)
    });
  }, [headersList, getFunction, setEditingFunction]);

  const removeHeader = useCallback((index) => {
    const newList = headersList.filter((_, i) => i !== index);
    setHeadersList(newList);
    
    // Update the function's headers
    const current = getFunction();
    setEditingFunction({
      ...current,
      api_headers: listToHeaders(newList)
    });
  }, [headersList, getFunction, setEditingFunction]);

  // Handle JSON text change for headers
  const handleHeadersJsonChange = useCallback((text) => {
    setJsonHeadersText(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      const current = getFunction();
      setEditingFunction({ ...current, api_headers: parsed });
      setHeadersList(headersToList(parsed));
    } catch (err) {
      setJsonError('Invalid JSON format');
    }
  }, [getFunction, setEditingFunction]);

  // ==================== PRESET HANDLER ====================

  const applyPreset = useCallback(async (preset) => {
    const config = { ...preset.config, api_headers: { ...(preset.config.api_headers || {}) } };

    // Auto-fill the tenant API key so the user doesn't have to paste anything.
    if (preset.injectApiKey) {
      try {
        let key = (await api.get('/auth/api-key')).data?.api_key;
        if (!key) key = (await api.post('/auth/api-key/generate')).data?.api_key;
        if (key) config.api_headers['x-api-key'] = key;
      } catch (e) {
        // Leave the header empty; the user can paste their key manually.
      }
    }

    setEditingFunction({ ...config });
    setParamsList(schemaToList(config.parameters));
    setHeadersList(headersToList(config.api_headers));
    setJsonParamsText(JSON.stringify(config.parameters, null, 2));
    setJsonHeadersText(JSON.stringify(config.api_headers || {}, null, 2));
  }, [setEditingFunction]);

  if (!isOpen) return null;

  const func = getFunction();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {func.id ? 'Edit Function' : 'Add Function'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-grow space-y-6">
          
          {/* Presets - Only show for new functions */}
          {!func.id && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Quick Presets (Telegram Integration)
              </p>
              <div className="flex flex-wrap gap-2">
                {TELEGRAM_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className={`px-3 py-1.5 text-sm text-white rounded-lg transition-colors flex items-center gap-1
                      ${preset.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                      ${preset.color === 'green' ? 'bg-green-600 hover:bg-green-700' : ''}
                      ${preset.color === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                    `}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Click a preset to auto-fill the form, then customize as needed
              </p>
            </div>
          )}

          {/* WhatsApp preset - Only show for new functions */}
          {!func.id && (
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Quick Presets (WhatsApp Integration)
              </p>
              <div className="flex flex-wrap gap-2">
                {WHATSAPP_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className="px-3 py-1.5 text-sm text-white rounded-lg transition-colors flex items-center gap-1 bg-green-600 hover:bg-green-700"
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Auto-fills everything (including your API key). Sends to the caller's
                WhatsApp by default; the bot can pass a different number to override.
              </p>
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Function Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={func.name || ''}
                onChange={(e) => updateField('name', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                placeholder="send_to_telegram"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Use lowercase with underscores (e.g., get_balance)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={func.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Describe what this function does - this helps the AI understand when to use it"
                rows={2}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  HTTP Method
                </label>
                <select
                  value={func.api_method || 'POST'}
                  onChange={(e) => updateField('api_method', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content Type
                </label>
                <select
                  value={func.content_type || 'application/json'}
                  onChange={(e) => updateField('content_type', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="application/json">JSON</option>
                  <option value="application/x-www-form-urlencoded">Form URL Encoded</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API URL <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={func.api_url || ''}
                onChange={(e) => updateField('api_url', e.target.value)}
                placeholder="http://localhost:3000/api/telegram/notify"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Parameters Section */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Parameters ({paramsList.length})</h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setParamsTab('form')}
                  className={`px-3 py-1 text-sm rounded-lg flex items-center gap-1 ${
                    paramsTab === 'form' 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-white border text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <ListPlus className="w-4 h-4" />
                  Form
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setParamsTab('json');
                    setJsonParamsText(JSON.stringify(func.parameters || { type: 'object', properties: {}, required: [] }, null, 2));
                  }}
                  className={`px-3 py-1 text-sm rounded-lg flex items-center gap-1 ${
                    paramsTab === 'json' 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-white border text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Code className="w-4 h-4" />
                  JSON
                </button>
              </div>
            </div>

            <div className="p-4">
              {paramsTab === 'form' ? (
                <div className="space-y-3">
                  {paramsList.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No parameters defined. Click "Add Parameter" to start.
                    </p>
                  ) : (
                    paramsList.map((param, index) => (
                      <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1 grid grid-cols-12 gap-2">
                          <div className="col-span-3">
                            <label className="block text-xs text-gray-500 mb-1">Name</label>
                            <input
                              type="text"
                              value={param.name}
                              onChange={(e) => updateParameter(index, 'name', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                              placeholder="param_name"
                              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-primary-500"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs text-gray-500 mb-1">Type</label>
                            <select
                              value={param.type}
                              onChange={(e) => updateParameter(index, 'type', e.target.value)}
                              className="w-full px-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-primary-500"
                            >
                              {PARAM_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-5">
                            <label className="block text-xs text-gray-500 mb-1">Description</label>
                            <input
                              type="text"
                              value={param.description}
                              onChange={(e) => updateParameter(index, 'description', e.target.value)}
                              placeholder="Description for AI"
                              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-primary-500"
                            />
                          </div>
                          <div className="col-span-2 flex items-end justify-center pb-1">
                            <label className="flex items-center gap-1 text-sm">
                              <input
                                type="checkbox"
                                checked={param.required || false}
                                onChange={(e) => updateParameter(index, 'required', e.target.checked)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              Required
                            </label>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 pt-5">
                          <button
                            type="button"
                            onClick={() => moveParameter(index, -1)}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveParameter(index, 1)}
                            disabled={index === paramsList.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeParameter(index)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded mt-5"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                  
                  <button
                    type="button"
                    onClick={addParameter}
                    className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-primary-400 hover:text-primary-600 flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Parameter
                  </button>
                </div>
              ) : (
                <div>
                  <textarea
                    value={jsonParamsText}
                    onChange={(e) => handleParamsJsonChange(e.target.value)}
                    rows={10}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                    placeholder="JSON Schema for parameters"
                  />
                  {jsonError && (
                    <p className="text-sm text-red-500 mt-1">{jsonError}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Headers Section */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Headers ({headersList.length})</h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setHeadersTab('form')}
                  className={`px-3 py-1 text-sm rounded-lg flex items-center gap-1 ${
                    headersTab === 'form' 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-white border text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <ListPlus className="w-4 h-4" />
                  Form
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setHeadersTab('json');
                    setJsonHeadersText(JSON.stringify(func.api_headers || {}, null, 2));
                  }}
                  className={`px-3 py-1 text-sm rounded-lg flex items-center gap-1 ${
                    headersTab === 'json' 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-white border text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Code className="w-4 h-4" />
                  JSON
                </button>
              </div>
            </div>

            <div className="p-4">
              {headersTab === 'form' ? (
                <div className="space-y-3">
                  {headersList.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-2">
                      No custom headers. Click "Add Header" if your API requires authentication.
                    </p>
                  ) : (
                    headersList.map((header, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-1/3">
                          <input
                            type="text"
                            value={header.key}
                            onChange={(e) => updateHeader(index, 'key', e.target.value)}
                            placeholder="Header-Name"
                            className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={header.value}
                            onChange={(e) => updateHeader(index, 'value', e.target.value)}
                            placeholder="Header value"
                            className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeHeader(index)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                  
                  <button
                    type="button"
                    onClick={addHeader}
                    className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-primary-400 hover:text-primary-600 flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Header
                  </button>
                </div>
              ) : (
                <textarea
                  value={jsonHeadersText}
                  onChange={(e) => handleHeadersJsonChange(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                  placeholder='{"Authorization": "Bearer YOUR_API_KEY"}'
                />
              )}
            </div>
          </div>

          {/* When to run */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              When to run
            </label>
            <select
              value={func.execution_mode || 'immediate'}
              onChange={(e) => updateField('execution_mode', e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="immediate">Every time the AI calls it (default)</option>
              <option value="once">Only once per call (first call wins)</option>
              <option value="call_end">Once, at the end of the call</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Use "at the end of the call" for notifications/summaries (e.g. send_to_telegram)
              so a single message is sent even if the AI triggers it multiple times.
            </p>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="func_active"
              checked={func.is_active !== false}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="func_active" className="text-sm text-gray-700">
              Function is active
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-between flex-shrink-0">
          {func.id ? (
            <button
              type="button"
              onClick={onDelete}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
            >
              Delete
            </button>
          ) : (
            <div />
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              {func.id ? 'Update' : 'Create'} Function
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}